import React, { useState, useEffect, useRef } from 'react';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { getTrackingLocations } from '../lib/api';
import { toast } from 'sonner';
import { MapPin, RefreshCw, Clock } from 'lucide-react';
import 'leaflet/dist/leaflet.css';

// Fix for default marker icons in React-Leaflet
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
    iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
    iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
    shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

// Custom green marker for employees
const employeeIcon = new L.Icon({
    iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-green.png',
    shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
    iconSize: [25, 41],
    iconAnchor: [12, 41],
    popupAnchor: [1, -34],
    shadowSize: [41, 41]
});

// Component to recenter map when locations change
const MapController = ({ locations }) => {
    const map = useMap();
    
    useEffect(() => {
        if (locations.length > 0) {
            const bounds = L.latLngBounds(locations.map(loc => [loc.latitude, loc.longitude]));
            map.fitBounds(bounds, { padding: [50, 50] });
        }
    }, [locations, map]);
    
    return null;
};

export const LiveTrackingPage = () => {
    const [locations, setLocations] = useState([]);
    const [loading, setLoading] = useState(true);
    const [lastUpdate, setLastUpdate] = useState(null);
    const intervalRef = useRef(null);

    useEffect(() => {
        loadLocations();
        // Auto-refresh every 30 seconds
        intervalRef.current = setInterval(loadLocations, 30000);
        
        return () => {
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const loadLocations = async () => {
        try {
            const res = await getTrackingLocations();
            setLocations(res.data);
            setLastUpdate(new Date());
        } catch (error) {
            // Silent fail
        } finally {
            setLoading(false);
        }
    };

    const formatTime = (isoString) => {
        if (!isoString) return '-';
        const date = new Date(isoString);
        return date.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });
    };

    // Default center: Germany
    const defaultCenter = [51.1657, 10.4515];
    const defaultZoom = 6;

    if (loading) {
        return (
            <div className="p-8 flex items-center justify-center min-h-screen">
                <div className="spinner" />
            </div>
        );
    }

    return (
        <div className="p-4 md:p-6 space-y-4 pb-24" data-testid="live-tracking-page">
            {/* Header */}
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-2">
                <div>
                    <h1 className="text-xl md:text-3xl font-bold flex items-center gap-2">
                        <MapPin className="h-6 w-6 text-primary" />
                        Live-Tracking
                    </h1>
                    <p className="text-sm text-muted-foreground">
                        {locations.length} Mitarbeiter eingestempelt
                    </p>
                </div>
                <div className="flex items-center gap-2">
                    {lastUpdate && (
                        <span className="text-xs text-muted-foreground">
                            {formatTime(lastUpdate.toISOString())}
                        </span>
                    )}
                    <Button onClick={loadLocations} variant="outline" size="sm" data-testid="refresh-btn">
                        <RefreshCw className="h-4 w-4" />
                    </Button>
                </div>
            </div>

            {/* Employee Cards - Mobile First */}
            {locations.length === 0 ? (
                <Card>
                    <CardContent className="p-6 text-center">
                        <MapPin className="h-10 w-10 mx-auto text-muted-foreground mb-3" />
                        <p className="font-medium">Keine aktiven Mitarbeiter</p>
                        <p className="text-sm text-muted-foreground">
                            Mitarbeiter erscheinen hier wenn sie am Terminal eingestempelt sind.
                        </p>
                    </CardContent>
                </Card>
            ) : (
                <div className="space-y-3">
                    {locations.map((loc) => (
                        <Card key={loc.user_id} className="overflow-hidden">
                            <CardContent className="p-4">
                                <div className="flex items-center gap-3">
                                    <div className="h-12 w-12 rounded-full bg-primary text-white flex items-center justify-center font-bold text-lg">
                                        {loc.user_name?.split(' ').map(n => n[0]).join('').slice(0, 2)}
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <p className="font-semibold truncate">{loc.user_name}</p>
                                        <p className="text-sm text-muted-foreground">
                                            Seit {loc.clocked_in_at || '-'} Uhr aktiv
                                        </p>
                                        <div className="flex items-center gap-1 mt-1">
                                            <span className="w-2 h-2 bg-primary rounded-full animate-pulse" />
                                            <span className="text-xs text-primary">Live • {formatTime(loc.timestamp)}</span>
                                        </div>
                                    </div>
                                </div>
                            </CardContent>
                        </Card>
                    ))}
                </div>
            )}

            {/* Map */}
            {locations.length > 0 && (
                <Card className="overflow-hidden">
                    <CardHeader className="pb-2 py-3">
                        <CardTitle className="text-base">Karte</CardTitle>
                    </CardHeader>
                    <CardContent className="p-0">
                        <div style={{ height: '300px', width: '100%' }} className="md:h-[400px]">
                            <MapContainer
                                center={locations.length > 0 ? [locations[0].latitude, locations[0].longitude] : defaultCenter}
                                zoom={locations.length > 0 ? 12 : defaultZoom}
                                style={{ height: '100%', width: '100%' }}
                            >
                                <TileLayer
                                    attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                                />
                                {locations.length > 0 && <MapController locations={locations} />}
                                {locations.map((loc) => (
                                    <Marker
                                        key={loc.user_id}
                                        position={[loc.latitude, loc.longitude]}
                                        icon={employeeIcon}
                                    >
                                        <Popup>
                                            <div className="p-1">
                                                <h3 className="font-bold">{loc.user_name}</h3>
                                                <p className="text-sm text-gray-600">
                                                    Seit {loc.clocked_in_at || '-'}
                                                </p>
                                            </div>
                                        </Popup>
                                    </Marker>
                                ))}
                            </MapContainer>
                        </div>
                    </CardContent>
                </Card>
            )}
        </div>
    );
};

export default LiveTrackingPage;
